import { TENANT_MAP_NODE_KEYS } from '../config/tenantNodeTabsConfig';
import { extractNodeListFromApiPayload } from './tenantNodeApiAdapter';
import { normalizeNodeRowsFromApi } from './tenantNodeRowAdapter';

const NODE_KEY_MAP = {
  facilities: 'facilities',
  products: 'products',
  logistics: 'logistics',
  logisticsNodes: 'logistics',
  geographies: 'geographies',
  commodities: 'commodities',
  supplierFacilities: 'supplierFacilities',
  supplier_facilities: 'supplierFacilities',
  graphRelationships: 'graphRelationships',
  graph_relationships: 'graphRelationships',
};

const normalizeRows = (nodeKey, raw) =>
  normalizeNodeRowsFromApi(nodeKey, extractNodeListFromApiPayload(raw));

export const mapGraphContextToNodesByKey = (context = {}) => {
  const nodesByKey = {};

  Object.entries(NODE_KEY_MAP).forEach(([sourceKey, targetKey]) => {
    const raw = context[sourceKey];
    if (raw == null) return;
    nodesByKey[targetKey] = normalizeRows(targetKey, raw);
  });

  return nodesByKey;
};

export const mapGraphContextCompanyRoutes = (context = {}) =>
  context.company_routes ?? context.companyRoutes ?? {};

/** Ensure all map-relevant node keys exist as arrays (empty when absent). */
export const normalizeMapNodesByKey = (nodesByKey = {}) => {
  const normalized = { ...nodesByKey };
  TENANT_MAP_NODE_KEYS.forEach((key) => {
    if (!Array.isArray(normalized[key])) {
      normalized[key] = [];
    }
  });
  return normalized;
};

const companyRouteEntries = (companyRoutes = {}) => {
  if (Array.isArray(companyRoutes)) return companyRoutes;
  if (!companyRoutes || typeof companyRoutes !== 'object') return [];
  return Object.entries(companyRoutes).map(([key, value]) => {
    if (!value || typeof value !== 'object') {
      return { companyId: key };
    }
    return {
      ...value,
      companyId: value.company_id ?? value.companyId ?? key,
    };
  });
};

/** Convert graph-context companyRoutes into watchlist-shaped items for map enrichment. */
export const mapCompanyRoutesToWatchlistItems = (companyRoutes = {}) =>
  companyRouteEntries(companyRoutes)
    .map((entry) => {
      const companyId = String(entry?.company_id ?? entry?.companyId ?? '').trim();
      if (!companyId) return null;

      const addresses =
        entry?.addresses ??
        entry?.user_addresses ??
        entry?.userAddresses ??
        entry?.userCompanyInfo?.userAddresses ??
        [];
      const routes =
        entry?.routes ?? entry?.userCompanyInfo?.routes ?? [];

      return {
        companyId,
        company_id: companyId,
        company: entry?.company ?? {
          name: entry?.company_name ?? entry?.companyName ?? companyId,
        },
        priority: entry?.priority ?? 'normal',
        userCompanyInfo: {
          userAddresses: Array.isArray(addresses) ? addresses : [],
          routes: Array.isArray(routes) ? routes : [],
        },
      };
    })
    .filter(Boolean);

export const hasCompanyRoutesContent = (companyRoutes = {}) =>
  mapCompanyRoutesToWatchlistItems(companyRoutes).some((item) => {
    const addresses = item.userCompanyInfo?.userAddresses ?? [];
    const routes = item.userCompanyInfo?.routes ?? [];
    return addresses.length > 0 || routes.length > 0;
  });

export const hasMapNodesInCache = (cached) => {
  const nodesByKey = cached?.nodesByKey;
  if (!nodesByKey || typeof nodesByKey !== 'object') return false;
  return TENANT_MAP_NODE_KEYS.some((key) => (nodesByKey[key]?.length ?? 0) > 0);
};
